import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import Checkbox from '@/shared/components/input/Checkbox';
import moment from 'moment';
import enumCache from '@/shared/services/EnumCacheService';
import utils from '@/shared/utils/adminpulse';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import messages from '../budgetMessages';

const currentYear = moment.utc().year();
const exampleAmount = 2500;

export default class UpdateBudgetAmountModal extends Component {
  state = {
    selectedUpdateMethod: 0,
    overrideBudget: false,
    referenceYear: currentYear - 1,
    budgetYear: currentYear,
    includeExForfait: false,
    amount: 0,
    selectedRoundingMethod: 0,
    roundingDecimals: 2,
    percentage: 0,
    percentageInput: '0',
    updateAmount: 0,
    updateAmountInput: '0',
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    year: PropTypes.number, // Year from filters
  };
  static defaultProps = {
    year: null,
  };
  componentDidMount() {
    const { intl, year } = this.props;
    const budgetYear = year || moment.utc().year();
    const updateMethods = [
      {
        id: 0,
        label: intl.formatMessage(messages.NewAmount),
      },
      {
        id: 1,
        label: intl.formatMessage(messages.UseForfaitAmountAsBudgetAmount),
      },
      {
        id: 2,
        label: intl.formatMessage(messages.UseRegistrationAmountAsBudgetAmount),
      },
      {
        id: 3,
        label: intl.formatMessage(messages.UseInvoiceAmountAsBudgetAmount),
      },
      {
        id: 4,
        label: intl.formatMessage(messages.UseAdvanceAmountAsBudgetAmount),
      },
      {
        id: 5,
        label: intl.formatMessage(messages.AddOrSubtractPercentage),
      },
      {
        id: 6,
        label: intl.formatMessage(messages.AddOrSubtractAmount),
      },
    ];

    Promise.all([enumCache.getTranslated('roundingMethod', intl)]).then(([roundingMethods]) => {
      const referenceYears = [budgetYear, budgetYear - 1, budgetYear - 2, budgetYear - 3];
      this.setState({
        referenceYear: budgetYear - 1,
        budgetYear,
        updateMethods,
        referenceYears,
        roundingMethods,
      });
    });
  }

  isFormInvalid = () => {
    const { referenceYear, selectedUpdateMethod, amount } = this.state;
    if (!referenceYear || referenceYear < 2000) return true;
    if (selectedUpdateMethod === 0 && (Number.isNaN(amount) || amount < 0)) return true;

    return false;
  };

  onSave = () => {
    const {
      overrideBudget,
      selectedUpdateMethod,
      referenceYear,
      budgetYear,
      includeExForfait,
      amount,
      selectedRoundingMethod,
      percentage,
      roundingDecimals,
      updateAmount,
    } = this.state;

    const { onSave } = this.props;

    const amountUpdate = {
      method: selectedUpdateMethod,
      overrideBudget,
      referenceYear,
      budgetYear,
      includeExForfait,
      roundingDecimals,
      amount,
      // Fields default to null while the user is mid-typing a negative
      // number (e.g. only '-' entered so far); fall back to 0 on save.
      percentage: percentage ?? 0,
      updateAmount: updateAmount ?? 0,
      rounding: selectedRoundingMethod,
    };

    return onSave(amountUpdate);
  };

  methodChanged = e => {
    const value = parseInt(e.target.value);
    this.setState({ selectedUpdateMethod: value, includeExForfait: value === 2 });
  };

  toggleCheckbox = name => {
    if (!name) return;
    this.setState(({ [name]: oldValue }) => ({ [name]: !oldValue }));
  };

  handleSignedAmountChange = (field, rawValue) => {
    const parsed = rawValue === '' || rawValue === '-' ? null : Number.parseFloat(rawValue);
    this.setState({
      [`${field}Input`]: rawValue,
      [field]: Number.isNaN(parsed) ? null : parsed,
    });
  };

  amountChanged = e => {
    let value = Number.parseFloat(e.target.value);
    if (Number.isNaN(value)) {
      value = null;
    } else {
      value = Math.max(0, Math.min(99999999999.99, value));
    }

    return this.setState({ amount: value });
  };
  generateExample = () => {
    const { percentage, selectedRoundingMethod, roundingDecimals, updateAmount, selectedUpdateMethod } = this.state;
    
    var method = selectedUpdateMethod === 6 ? 1 : 0;
    var amountOrPercentage = selectedUpdateMethod === 6 ? updateAmount : percentage;

    return utils.calculateIndexedAmount(
      Number.parseFloat(exampleAmount),
      method,
      amountOrPercentage,
      selectedRoundingMethod,
      roundingDecimals,
    );
  };
  render() {
    const { show, onClose, intl } = this.props;
    const {
      overrideBudget,
      selectedUpdateMethod,
      updateMethods,
      referenceYear,
      budgetYear,
      includeExForfait,
      amount,
      referenceYears,
      roundingMethods,
      selectedRoundingMethod,
      roundingDecimals,
      percentageInput,
      updateAmountInput,
    } = this.state;

    if (!show) return null;
    const generatedAmount = this.generateExample();

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.UpdateAmountForYear, { year: budgetYear })}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-3">{intl.formatMessage(messages.Method)}</label>
            <div className="col-sm-8">
              {updateMethods && (
                <select
                  onChange={this.methodChanged}
                  className="form-control"
                  value={selectedUpdateMethod}
                >
                  {updateMethods.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {selectedUpdateMethod !== 0 && selectedUpdateMethod !== 4 ? (
            <div className="form-group">
              <label className="control-label col-sm-3">
                {intl.formatMessage(messages.BudgetBasedOnYear)}
              </label>
              <div className="col-sm-8">
                <select
                  className="form-control"
                  value={referenceYear || ''}
                  onChange={e => this.setState({ referenceYear: parseInt(e.target.value) || null })}
                >
                  {referenceYears.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {selectedUpdateMethod === 0 ? (
            <div className="form-group">
              <label className="control-label col-sm-3">
                {intl.formatMessage(messages.Amount)}
              </label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  id="amount"
                  min={0}
                  max="99999999999.99"
                  step="any"
                  value={amount || ''}
                  onChange={this.amountChanged}
                />
              </div>
            </div>
          ) : null}

          {selectedUpdateMethod === 5 && (
            <>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Percentage)}
                </label>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="number"
                      step="1"
                      value={percentageInput}
                      onChange={e => this.handleSignedAmountChange('percentage', e.target.value)}
                      className="form-control"
                    />
                    <span className="input-group-addon">%</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.RoundingMethod)}
                </label>
                <div className="col-sm-8">
                  {roundingMethods && (
                    <select
                      onChange={e =>
                        this.setState({ selectedRoundingMethod: parseInt(e.target.value) })
                      }
                      className="form-control"
                      value={selectedRoundingMethod}
                    >
                      {roundingMethods.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.RoundTo)}
                </label>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="number"
                      onChange={e => this.setState({ roundingDecimals: parseInt(e.target.value) })}
                      className="form-control"
                      min={0}
                      max={5}
                      value={roundingDecimals}
                    />
                    <span className="input-group-addon">{intl.formatMessage(messages.Digits)}</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.Example)}
                </label>
                <div className="col-sm-5">
                  <div className="form-control-static inline-block">
                    <FormattedCurrency value={exampleAmount} maximumFractionDigits={5} />
                    <div className="text-center">&darr;</div>
                    {!Number.isNaN(generatedAmount) && (
                      <FormattedCurrency value={generatedAmount} maximumFractionDigits={5} />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {selectedUpdateMethod === 6  && (
            <>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Amount)}
                </label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    step="1"
                    value={updateAmountInput}
                    onChange={e => this.handleSignedAmountChange('updateAmount', e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.Example)}
                </label>
                <div className="col-sm-5">
                  <div className="form-control-static inline-block">
                    <FormattedCurrency value={exampleAmount} maximumFractionDigits={5} />
                    <div className="text-center">&darr;</div>
                    {!Number.isNaN(generatedAmount) && (
                      <FormattedCurrency value={generatedAmount} maximumFractionDigits={5} />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {selectedUpdateMethod === 1 ? (
            <div className="form-group">
              <Checkbox
                extraClass="col-sm-offset-3 col-md-8"
                id="includeExForfait"
                value={includeExForfait}
                onClick={() => this.toggleCheckbox('includeExForfait')}
                text={intl.formatMessage(messages.IncludeExForfaitRegistrations)}
              />
            </div>
          ) : null}

          <div className="form-group">
            <Checkbox
              extraClass="col-sm-offset-3 col-md-8"
              id="overrideBudget"
              value={overrideBudget}
              onClick={() => this.setState({ overrideBudget: !overrideBudget })}
              text={intl.formatMessage(messages.OverrideIfBudgetExists)}
            />
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
